import { useNavigate } from "#src/Routers/hooks";
import { InvestigationDetailRoutePath } from "#src/routes/investigations/[investigationId]";
import {
  getInvestigationDisplayTitle,
  InvestigationStatusToPillVariantMapping,
  isTerminalInvestigationStatus,
} from "#src/view-models/investigations";
import { Investigations } from "@validereinc/domain-controllers";
import {
  DateDataDisplay,
  Icon,
  ListCard,
  Pill,
  TextDataDisplay,
} from "@validereinc/common-components";
import type { DropdownMenuOption } from "@validereinc/common-components";
import type { InvestigationType } from "@validereinc/domain";
import classNames from "classnames/bind";
import React, { useCallback, useMemo } from "react";
import styles from "./InvestigationHistoryCard.module.scss";

const cx = classNames.bind(styles);

type InvestigationHistoryCardTitleRowProps = {
  displayTitle: string;
  status: InvestigationType["status"];
  isArchived: boolean;
};

const InvestigationHistoryCardTitleRow = ({
  displayTitle,
  status,
  isArchived,
}: InvestigationHistoryCardTitleRowProps) => (
  <div className={cx("title-row")}>
    <TextDataDisplay
      className={cx("title-row__title")}
      value={displayTitle}
      clamp={1}
    />
    <div className={cx("title-row__pill")}>
      <Pill
        variant={
          isArchived
            ? "default"
            : InvestigationStatusToPillVariantMapping[status]
        }
        hasDot={false}
      >
        {isArchived
          ? "Archived"
          : Investigations.view.InvestigationStatusLabel[status]}
      </Pill>
    </div>
  </div>
);

type InvestigationHistoryCardProps = {
  investigation: InvestigationType;
  isSelected: boolean;
  hasBorderBottom?: boolean;
  isActionsMenuOpen: boolean;
  onActionsMenuOpenChange: (isOpen: boolean) => void;
  onDismissActionsMenu: () => void;
  onRename: (investigation: InvestigationType) => void;
  onSetArchived: (investigation: InvestigationType, archived: boolean) => void;
};

export const InvestigationHistoryCard = ({
  investigation,
  isSelected,
  hasBorderBottom = true,
  isActionsMenuOpen,
  onActionsMenuOpenChange,
  onDismissActionsMenu,
  onRename,
  onSetArchived,
}: InvestigationHistoryCardProps) => {
  const navigate = useNavigate();
  const displayTitle = getInvestigationDisplayTitle(investigation);

  const handleSelectInvestigation = useCallback(() => {
    onDismissActionsMenu();
    navigate({
      pathname: InvestigationDetailRoutePath.toLink({
        pathParams: { investigationId: investigation.id },
      }),
    });
  }, [investigation.id, navigate, onDismissActionsMenu]);

  const handleRenameClick = useCallback(() => {
    onRename(investigation);
  }, [investigation, onRename]);

  const canSetArchived =
    investigation.archived ||
    isTerminalInvestigationStatus(investigation.status);
  const handleSetArchivedClick = useCallback(() => {
    onSetArchived(investigation, !investigation.archived);
  }, [investigation, onSetArchived]);

  const investigationHistoryCardActions = useMemo(
    (): DropdownMenuOption[] => [
      {
        label: "Rename",
        onClick: handleRenameClick,
        slotLeft: ({ iconProps }) => (
          <Icon
            variant="pencil-simple"
            {...iconProps}
          />
        ),
      },
      {
        label: investigation.archived ? "Unarchive" : "Archive",
        isDisabled: !canSetArchived,
        tooltip: canSetArchived
          ? undefined
          : "Archive is available once the investigation is idle.",
        onClick: handleSetArchivedClick,
        slotLeft: ({ iconProps }) => (
          <Icon
            variant={"archive"}
            {...iconProps}
          />
        ),
      },
    ],
    [canSetArchived, handleRenameClick, handleSetArchivedClick, investigation]
  );

  return (
    <ListCard
      className={cx("history-card")}
      isSelected={isSelected}
      hasBorderBottom={hasBorderBottom}
      style={
        isSelected && hasBorderBottom
          ? { borderBottomColor: "var(--colour-surface-200)" }
          : undefined
      }
      renderTitle={({ FieldSegment }) => (
        <FieldSegment className={cx("title-field")}>
          <InvestigationHistoryCardTitleRow
            displayTitle={displayTitle}
            status={investigation.status}
            isArchived={investigation.archived}
          />
        </FieldSegment>
      )}
      renderMeta={({ FieldSegment }) => (
        <FieldSegment>
          Created{" "}
          <DateDataDisplay
            value={investigation.created_at}
            isInline
          />
        </FieldSegment>
      )}
      actions={investigationHistoryCardActions}
      actionsMenuContentProps={{ side: "right" }}
      actionsMenuOpen={isActionsMenuOpen}
      onActionsMenuOpenChange={onActionsMenuOpenChange}
      onClick={handleSelectInvestigation}
    />
  );
};
